import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../services/auth';
import type { RegisterData } from '../../types/auth';

interface RegisterFormData extends RegisterData {
  confirmPassword: string;
}

interface RegisterStatus {
  error: string;
  success: boolean;
}

export const Register: React.FC = () => {
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState<RegisterFormData>({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
  });

  const [status, setStatus] = useState<RegisterStatus>({
    error: '',
    success: false,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
    // Clear error when user starts typing
    if (status.error) {
      setStatus(prev => ({ ...prev, error: '' }));
    }
  };

  const validateForm = (): string | null => {
    if (!formData.firstName.trim()) return 'First name is required';
    if (!formData.lastName.trim()) return 'Last name is required';
    if (!formData.email.trim()) return 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) return 'Please enter a valid email';
    if (formData.password.length < 6) return 'Password must be at least 6 characters';
    if (formData.password !== formData.confirmPassword) return 'Passwords do not match';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setStatus(prev => ({ ...prev, error: validationError }));
      return;
    }

    setStatus({ error: '', success: false });

    try {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { confirmPassword, ...registerData } = formData;
      
      await authService.register({
        ...registerData,
        email: registerData.email.trim(),
        firstName: registerData.firstName.trim(),
        lastName: registerData.lastName.trim(),
      });

      setStatus({
        error: '',
        success: true,
      });
      
      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/login', { 
          state: { 
            message: 'Registration successful! Please sign in with your credentials.' 
          }
        });
      }, 2000);
    } catch (error) {
      console.error('Registration error:', error);
      setStatus({
        error: error instanceof Error ? error.message : 'Registration failed. Please try again.',
        success: false,
      });
    }
  };

  const handleLoginRedirect = () => {
    navigate('/login');
  };

  const isFormValid = () => {
    return formData.firstName.trim() && 
           formData.lastName.trim() && 
           formData.email.trim() && 
           formData.password.length >= 6 && 
           formData.password === formData.confirmPassword;
  };

  if (status.success) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-header">
            <div className="auth-brand">
              <img src="/img/icon-f.png" alt="Suvadi" className="auth-logo" />
              <h1 className="auth-title">
                <span className="tamil tamil-highlight">சு</span>
                <span className="tamil tamil-accent">வடி</span> Suvadi
              </h1>
            </div>
            <p className="auth-subtitle">Registration Successful!</p>
          </div>
          <div className="auth-success">
            <span className="auth-success-icon">✅</span>
            <p>Your account has been created successfully. You can now sign in with your credentials.</p>
            <p><strong>Redirecting you to sign in...</strong></p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-header">
          <div className="auth-brand">
            <img src="/img/icon-f.png" alt="Suvadi" className="auth-logo" />
            <h1 className="auth-title">
              <span className="tamil tamil-highlight">சு</span>
              <span className="tamil tamil-accent">வடி</span> Suvadi
            </h1>
          </div>
          <p className="auth-subtitle">Join your digital heritage</p>
        </div>

        <form className="auth-form" onSubmit={handleSubmit}>
          {status.error && (
            <div className="auth-error" role="alert">
              <span className="auth-error-icon">⚠️</span>
              {status.error}
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName" className="form-label">
                First Name
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                className="form-input"
                placeholder="Enter your first name"
                required
                autoComplete="given-name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="lastName" className="form-label">
                Last Name
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                className="form-input"
                placeholder="Enter your last name"
                required
                autoComplete="family-name"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="email" className="form-label">
              Email Address
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="form-input"
              placeholder="Enter your email"
              required
              autoComplete="email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password" className="form-label">
              Password
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className="form-input"
              placeholder="Create a password (min. 6 characters)"
              required
              autoComplete="new-password"
              minLength={6}
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword" className="form-label">
              Confirm Password
            </label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              className="form-input"
              placeholder="Confirm your password"
              required
              autoComplete="new-password"
            />
          </div>

          <button
            className="auth-button auth-button-primary"
            disabled={!isFormValid()}
            type="submit"
          >
            Create Account
          </button>

          <div className="auth-footer">
            <p className="auth-footer-text">
              Already have an account?{' '}
              <button
                type="button"
                onClick={handleLoginRedirect}
                className="auth-link"
              >
                Sign In
              </button>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};
